//@flow
/* eslint-disable react/jsx-no-bind */
import * as React from 'react';
import {
    Text,
    Flex,
    Box,
    Icon,
    Stack,
    Columns,
    Column
} from '@locus-taxy/neo-ui';
import { type TFunction } from 'react-i18next';
import { type FpBeat } from 'common/models/internal/FpBeat';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

type Props = {
    legendMap: Map<string, Object>,
    isMapOptionsOpen: boolean,
    getContainer: () => string,
    visibleBeats: Array<FpBeat>,
    filteredBeats: Array<FpBeat>,
    t: TFunction,
    unitConverter: UnitConversionHelper,
    polygon: boolean,
    beatSelectionMap: Map<string, boolean>,
    polygonOption: string
};

const FpMapBeatMetricBox = (props: Props) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const onToggleView = isOpen => !isOpen;

    const {
        polygonOption,
        beatSelectionMap,
        visibleBeats,
        filteredBeats,
        UnitConverter,
        t
    } = props;

    const _getSelectedBeatMetrics = () => {
        let totalDuration = 0;
        let totalTransactionDuration = 0;
        let totalTravelDistance = 0;
        let totalTravelDuration = 0;
        let totalVisitCount = 0;
        let metricBeats;
        if (polygonOption === PolygonOptionTypes.SELECTED) {
            metricBeats = visibleBeats.filter(beat => {
                return beatSelectionMap.get(beat.beatId);
            });
        } else {
            metricBeats = [...filteredBeats];
        }
        metricBeats.forEach(beat => {
            totalDuration = totalDuration + beat.summary.totalDuration;
            totalTransactionDuration =
                totalTransactionDuration +
                beat.summary.totalTransactionDuration;
            totalTravelDistance =
                totalTravelDistance + beat.summary.totalTravelDistance;
            totalTravelDuration =
                totalTravelDuration + beat.summary.totalTravelDuration;
            totalVisitCount = totalVisitCount + beat.summary.visitCount;
        });
        const formattedTotalDuration = UnitConverter.convertSeconds({
            value: totalDuration
        });
        const formattedTotalTransactionDuration = UnitConverter.convertSeconds({
            value: totalTransactionDuration
        });
        const formattedTotalTravelDistance = UnitConverter.convertMeters({
            value: totalTravelDistance
        });
        const formattedTotalTravelDuration = UnitConverter.convertSeconds({
            value: totalTravelDuration
        });

        return (
            <Box p={1}>
                <Stack space="small">
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={1} color="white">
                                {t('fpPlanView.totalDuration')}
                            </Text>
                        </Column>
                        <Column width="1/3">
                            <Text fontSize={1} color="white">
                                {`${formattedTotalDuration.value} ${formattedTotalDuration.unit}`}
                            </Text>
                        </Column>
                    </Columns>
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={1} color="white">
                                {t('fpPlanView.totalTransactionDuration')}
                            </Text>
                        </Column>
                        <Column width="1/3">
                            <Text fontSize={1} color="white">
                                {`${formattedTotalTransactionDuration.value} ${formattedTotalTransactionDuration.unit}`}
                            </Text>
                        </Column>
                    </Columns>
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={1} color="white">
                                {t('fpPlanView.totalTravelDistance')}
                            </Text>
                        </Column>
                        <Column width="1/3">
                            <Text
                                fontSize={1}
                                color="white"
                            >{`${formattedTotalTravelDistance.value} ${formattedTotalTravelDistance.unit}`}</Text>
                        </Column>
                    </Columns>
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={1} color="white">
                                {t('fpPlanView.totalTravelDuration')}
                            </Text>
                        </Column>
                        <Column width="1/3">
                            <Text
                                fontSize={1}
                                color="white"
                            >{`${formattedTotalTravelDuration.value} ${formattedTotalTravelDuration.unit}`}</Text>
                        </Column>
                    </Columns>
                    <Columns alignY="center">
                        <Column width="fluid">
                            <Text fontSize={1} color="white">
                                {t('fpPlanView.totalVisitCount')}
                            </Text>
                        </Column>
                        <Column width="1/3">
                            <Text fontSize={1} color="white">
                                {totalVisitCount}
                            </Text>
                        </Column>
                    </Columns>
                </Stack>
            </Box>
        );
    };

    const beatMetrics = _getSelectedBeatMetrics();

    return (
        <Box
            sx={{
                height: isOpen ? '10rem' : '3rem',
                transition: '0.5s ease height'
            }}
        >
            <Flex
                height={32}
                alignItems="center"
                px={2}
                py={1}
                bg="black"
                sx={{
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    borderBottomLeftRadius: isOpen ? 0 : '0.5rem',
                    borderBottomRightRadius: isOpen ? 0 : '0.5rem'
                }}
                onClick={() => {
                    setIsOpen(onToggleView);
                }}
            >
                <Box>
                    <Icon
                        color="white"
                        icon={isOpen ? 'arrow_drop_down' : 'arrow_drop_right'}
                    />
                </Box>
                <Box px={2}>
                    <Text fontSize={0} color="white">
                        {`Beat Metrics (${polygonOption.toUpperCase()})`}
                    </Text>
                </Box>
            </Flex>
            <Box
                height="18rem"
                p={1}
                bg="black"
                width="20rem"
                sx={{
                    visibility: isOpen ? 'visible' : 'hidden',
                    opacity: isOpen ? 1 : 0,
                    borderBottomLeftRadius: '0.5rem',
                    borderBottomRightRadius: '0.5rem',
                    overflow: 'hidden'
                }}
            >
                {beatMetrics}
            </Box>
        </Box>
    );
};

export default FpMapBeatMetricBox;
